import React, { useEffect, useState } from 'react';
import API from '../services/api';
import Button from '../ui/Button';

function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    password: '',
    // Extended profile fields for therapists/users
    dob: '',
    hireDate: '',
    emergencyContactName: '',
    emergencyContactPhone: ''
  });
  // Limited-admin permission catalog + the grants for the user being edited.
  const [permCatalog, setPermCatalog] = useState([]);
  const [permsForm, setPermsForm] = useState([]);

  useEffect(() => {
    API.get('/admin/permissions/catalog')
      .then((res) => setPermCatalog(res.data?.areas || []))
      .catch(() => {});
  }, []);

  const togglePerm = (key) =>
    setPermsForm((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const fetchUsers = async () => {
    try {
      // Always fetch all users, and filter client-side if needed
      const res = await API.get('/admin/users/all');
      
      // If showInactive is false, filter out inactive users
      if (!showInactive) {
        setUsers(res.data.filter(user => user.is_active));
      } else {
        setUsers(res.data);
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching users');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [showInactive]);

  const handleEditClick = (user) => {
    setEditingUserId(user.id);
    setEditForm({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: user.role,
      password: '',
      // Prepopulate extended fields; convert dates to ISO strings
      dob: user.dob ? user.dob.slice(0, 10) : '',
      hireDate: user.hire_date ? user.hire_date.slice(0, 10) : '',
      emergencyContactName: user.emergency_contact_name || '',
      emergencyContactPhone: user.emergency_contact_phone || ''
    });
    setPermsForm(Array.isArray(user.limited_permissions) ? user.limited_permissions : []);
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      password: '',
      dob: '',
      hireDate: '',
      emergencyContactName: '',
      emergencyContactPhone: ''
    });
  };

  const handleEditFormChange = (e) => {
    setEditForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSaveEdit = async (userId) => {
    try {
      await API.put(`/admin/users/${userId}`, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        role: editForm.role,
        password: editForm.password || null,
        // Include extended fields in the payload
        dob: editForm.dob || null,
        hireDate: editForm.hireDate || null,
        emergencyContactName: editForm.emergencyContactName || null,
        emergencyContactPhone: editForm.emergencyContactPhone || null
      });
      // For a Limited Admin, also save their granted areas.
      if (editForm.role === 'LIMITED_ADMIN') {
        await API.put(`/admin/users/${userId}/permissions`, { permissions: permsForm });
      }
      alert('User updated!');
      setEditingUserId(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Error updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await API.delete(`/admin/users/${userId}`);
      alert('User deleted');
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Error deleting user');
    }
  };

  // New function to toggle user active status
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await API.put(`/admin/users/${userId}/active`, {
        isActive: !currentStatus
      });
      alert(currentStatus ? 'User deactivated successfully' : 'User activated successfully');
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Error updating user status');
    }
  };

  const actionBtn = { padding: '6px 12px', fontSize: 13 };

  return (
    <div>
      <h2>Manage Users</h2>
      <div style={{ marginBottom: '1rem' }}>
        <label>
          <input 
            type="checkbox" 
            checked={showInactive} 
            onChange={() => setShowInactive(!showInactive)}
          />
          Show inactive users
        </label>
      </div>
      <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>Role</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            if (editingUserId === u.id) {
              // Editing
              return (
                <tr key={u.id}>
                  <td>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </td>
                  <td>
                    <select
                      name="role"
                      value={editForm.role}
                      onChange={handleEditFormChange}
                    >
                      <option value="THERAPIST">THERAPIST</option>
                      <option value="LIMITED_ADMIN">LIMITED_ADMIN</option>
                      <option value="OFFICE_ADMIN">OFFICE_ADMIN</option>
                      <option value="PORTAL_ADMIN">PORTAL_ADMIN</option>
                    </select>
                  </td>
                  <td>
                    <input
                      name="firstName"
                      value={editForm.firstName}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      name="lastName"
                      value={editForm.lastName}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      name="email"
                      type="email"
                      value={editForm.email}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <div>
                      {/* Password field */}
                      <label style={{ fontSize: '0.8rem', display: 'block' }}>
                        New Password (optional):
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={editForm.password}
                        onChange={handleEditFormChange}
                      />

                      {/* Date of Birth field */}
                      <label style={{ fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                        Date of Birth:
                      </label>
                      <input
                        type="date"
                        name="dob"
                        value={editForm.dob}
                        onChange={handleEditFormChange}
                      />

                      {/* Hire Date field */}
                      <label style={{ fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                        Hire Date:
                      </label>
                      <input
                        type="date"
                        name="hireDate"
                        value={editForm.hireDate}
                        onChange={handleEditFormChange}
                      />

                      {/* Emergency Contact Name field */}
                      <label style={{ fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                        Emergency Contact Name:
                      </label>
                      <input
                        type="text"
                        name="emergencyContactName"
                        value={editForm.emergencyContactName}
                        onChange={handleEditFormChange}
                      />

                      {/* Emergency Contact Phone field */}
                      <label style={{ fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                        Emergency Contact Phone:
                      </label>
                      <input
                        type="text"
                        name="emergencyContactPhone"
                        value={editForm.emergencyContactPhone}
                        onChange={handleEditFormChange}
                      />

                      {/* Limited Admin — grantable areas */}
                      {editForm.role === 'LIMITED_ADMIN' && (
                        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.6rem', border: '1px solid var(--border-color)', borderRadius: 6, background: '#f9fafb' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 4 }}>Limited Admin access</div>
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 6 }}>
                            Check the areas this limited admin may use. Unchecked areas stay blocked.
                          </div>
                          {permCatalog.map((a) => (
                            <label key={a.key} style={{ display: 'block', fontSize: '0.82rem', margin: '3px 0' }} title={a.description}>
                              <input
                                type="checkbox"
                                checked={permsForm.includes(a.key)}
                                onChange={() => togglePerm(a.key)}
                              />{' '}
                              {a.label}
                            </label>
                          ))}
                        </div>
                      )}

                      <br />
                      <Button variant="primary" style={actionBtn} onClick={() => handleSaveEdit(u.id)}>Save</Button>
                      <Button variant="subtle" style={{ ...actionBtn, marginLeft: 6 }} onClick={handleCancelEdit}>Cancel</Button>
                    </div>
                  </td>
                </tr>
              );
            } else {
              // Viewing
              return (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.6 }}>
                  <td>
                    <span style={{
                      color: u.is_active ? 'var(--success-color)' : 'var(--error-color)',
                      fontWeight: 'bold'
                    }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{u.role}</td>
                  <td>{u.first_name}</td>
                  <td>{u.last_name}</td>
                  <td>{u.email}</td>
                  <td>
                    <Button variant="ghost" style={actionBtn} onClick={() => handleEditClick(u)}>Edit</Button>
                    <Button
                      variant={u.is_active ? 'danger' : 'success'}
                      style={{ ...actionBtn, marginLeft: 5, marginRight: 5 }}
                      onClick={() => toggleUserStatus(u.id, u.is_active)}
                    >
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="danger" style={actionBtn} onClick={() => handleDeleteUser(u.id)}>Delete</Button>
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default AdminUsersPage;